/**
 * React Query hooks for Preferences
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getUserPreferences,
  getDishPreferences,
  setDishPreference,
  removeDishPreference,
  getIngredientPreferences,
  setIngredientPreference,
  removeIngredientPreference,
} from '@/lib/api/preferences';
import type {
  SetDishPreferenceRequest,
  SetIngredientPreferenceRequest,
} from '@/types/api';
import { preferenceKeys } from '@/lib/query-keys';

export { preferenceKeys };

export function useUserPreferences(userId: number) {
  return useQuery({
    queryKey: preferenceKeys.user(userId),
    queryFn: () => getUserPreferences(userId),
    enabled: Number.isFinite(userId) && userId > 0,
  });
}

export function useDishPreferences(dishId: number) {
  return useQuery({
    queryKey: preferenceKeys.dish(dishId),
    queryFn: () => getDishPreferences(dishId),
    enabled: Number.isFinite(dishId) && dishId > 0,
  });
}

export function useIngredientPreferences(productId: number) {
  return useQuery({
    queryKey: preferenceKeys.ingredient(productId),
    queryFn: () => getIngredientPreferences(productId),
    enabled: Number.isFinite(productId) && productId > 0,
  });
}

export function useSetDishPreference() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      dishId,
      data,
    }: {
      dishId: number;
      data: SetDishPreferenceRequest;
    }) => setDishPreference(dishId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: preferenceKeys.all });
    },
  });
}

export function useRemoveDishPreference() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ dishId, userId }: { dishId: number; userId: number }) =>
      removeDishPreference(dishId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: preferenceKeys.all });
    },
  });
}

export function useSetIngredientPreference() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      productId,
      data,
    }: {
      productId: number;
      data: SetIngredientPreferenceRequest;
    }) => setIngredientPreference(productId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: preferenceKeys.all });
    },
  });
}

export function useRemoveIngredientPreference() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, userId }: { productId: number; userId: number }) =>
      removeIngredientPreference(productId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: preferenceKeys.all });
    },
  });
}
